import React, { memo, useCallback } from 'react'

import { GameId, isStoreOfferProduct, ProductV2, StoreOfferProduct } from '@common'
import cx from 'classnames'
import Image from 'next/image'
import { useTranslations } from 'next-intl'

import EventBackground from '@/assets/images/storefronts/clashofclans/passcard-event-bg.png'
import { scrollModal } from '@/components/Modal'
import { useMarketContext } from '@/contexts/MarketContext'
import { useModalContext } from '@/contexts/ModalContext'
import { useIsLoggedIn } from '@/features/common/useIsLoggedIn'
import { BuyButton } from '@/features/storefronts/_storeBase/BuyButton/BuyButton'
import { getRibbonsFromTags } from '@/features/storefronts/_storeBase/card/v2/OfferCard'
import { Ribbon, Ribbons } from '@/features/storefronts/_storeBase/card/v2/OfferCard/shared/Ribbons'
import { Countdown, Tags, ValueBadge } from '@/features/storefronts/_storeBase/card/v2/shared'
import { getButtonVariant } from '@/features/storefronts/_storeBase/card/v2/shared/Footer'
import { Flare } from '@/features/storefronts/_storeBase/flare/Flare'
import { StampCard } from '@/features/storefronts/_storeBase/stampCard/StampCard'
import { useDescription } from '@/features/storefronts/_storeBase/stampCard/StampCardRewardInfo'
import { StoreTypography } from '@/features/storefronts/_storeBase/typography/StoreTypography'
import { useGameSlug } from '@/features/storefronts/game-provider'
import { useTranslatable } from '@/localization/useTranslatable'
import { resolveProductImage } from '@/utils/resolveProductImage'

import { isGoldPass } from '../../../../../productFilters'

import styles from './ClashPassCard.module.scss'

interface PassCardProps {
  product: ProductV2
  productPage?: boolean
  enableNewVisuals?: GameId[]
}

const ClashPassHero = ({ product }: { product: ProductV2 }) => {
  const translatable = useTranslatable()
  const isGoldPassProduct = isGoldPass(product)
  return (
    <div className={styles.PassHero}>
      <div className={styles.BgImageContainer}>
        {isGoldPassProduct ? (
          <div className={styles.GoldBg} />
        ) : (
          <Image src={EventBackground} alt="Passcard background" width={343} height={178} quality={100} />
        )}
      </div>
      <Flare game={'clashofclans'} blurryEllipsis={false} />
      <div className={styles.ProductImageContainer}>
        <div className={styles.ProductImage}>
          <Image
            className={styles.PassImage}
            src={resolveProductImage(product)}
            fill
            sizes={'360px'}
            quality={100}
            alt="passcard-background"
            placeholder="empty"
          />
        </div>
      </div>
      <div className={styles.TitleContainer}>
        <StoreTypography tag="h3" textSize="lg" style="outlined" className={styles.Title}>
          {translatable(product.title, 'title')}
        </StoreTypography>
      </div>
    </div>
  )
}

export const ClashPassCard: React.FC<PassCardProps> = memo(({ product, enableNewVisuals }) => {
  const gameSlug = useGameSlug()
  const isLoggedIn = useIsLoggedIn()
  const market = useMarketContext('BrawlPass')

  const { openModal } = useModalContext()
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement, MouseEvent>) => {
      // Resort to default behavior if user is holding down the ctrl or meta key
      if (event.ctrlKey || event.metaKey) {
        return
      }
      // Prevent opening the product page on background when user clicks the card
      event.preventDefault()

      openModal({ type: 'PRODUCT', props: { product, enableNewVisuals } })
      // Scroll modal to top
      scrollModal(0, 0)
    },
    [product, openModal, enableNewVisuals],
  )
  const pathname = `/${gameSlug}/product/${product.slug}`

  const isSeasonPassProduct = isGoldPass(product) || !!product.contentCounts.SEASON_PASS

  let ribbons: Ribbon[] = []
  if (isStoreOfferProduct(product) && product.tags !== undefined) {
    ribbons = getRibbonsFromTags(product)
  }
  return isSeasonPassProduct ? (
    <a href={pathname} className={styles.PassCard} onClick={handleClick}>
      <Countdown product={product as StoreOfferProduct} />
      {ribbons.length === 0 ? <ValueBadge product={product as StoreOfferProduct} /> : <Ribbons ribbons={ribbons} />}
      <div className={styles.PassContainer}>
        <ClashPassHero product={product} />
        <div className={cx(styles.Footer, styles[market.currencyCode])}>
          <Tags product={product} variant={'Dark'} />
          <BuyButton
            style={{ height: '48px' }}
            variant={getButtonVariant(product)}
            product={product}
            data-cy={`bundle-${product.id}`}
          />
        </div>
      </div>
      {isGoldPass(product) && (
        <>
          {isLoggedIn && (
            <div>
              <div className={styles.RopeChain}>
                <div className={styles.Rope}></div>
                <div className={styles.Rope}></div>
              </div>
              <StamRopeChainInfo product={product} />
            </div>
          )}
          <div>
            <div className={styles.RopeChain}>
              <div className={styles.Rope}></div>
              <div className={styles.Rope}></div>
            </div>
            <StreakBonusInfo />
          </div>
        </>
      )}
    </a>
  ) : null
})

type ChainInfoProps = {
  product: ProductV2
  hideInfo?: boolean
  itemsNeeded?: number
}
const StamRopeChainInfo = ({ itemsNeeded, product }: ChainInfoProps) => {
  const t = useTranslations()
  const keys = { product: 'gold_pass', item: 'book_of_everything' } as const
  const description = useDescription(keys, itemsNeeded)
  const title = t(`stamps_${keys.product}`)

  return (
    <div className={cx(styles.RopeSign, styles.StampInfo)}>
      <div>
        <div className={styles.StampCardContainer}>
          <StampCard product={product} size={'small'} />
        </div>
      </div>
      <div className={styles.RopeSignContent}>
        <StoreTypography
          tag="h5"
          style="plain"
          textSize="sm"
          overrideFont="supercellTextBold"
          lineHeight="normal"
          className={styles.stampDescriptionHeading}
        >
          {title}
        </StoreTypography>
        <StoreTypography
          tag="p"
          style="plain"
          textSize="sm"
          overrideFont="supercellText"
          lineHeight="normal"
          className={styles.stampDescriptionText}
        >
          {description}
        </StoreTypography>
      </div>
    </div>
  )
}
const StreakBonusInfo = () => {
  const t = useTranslations()
  return (
    <div className={cx(styles.RopeSign, styles.StampInfo)}>
      <div className={styles.StreakBonusImageContainer}>
        <div className={styles.StreakBonusImage}></div>
      </div>
      <div className={styles.RopeSignContent}>
        <StoreTypography
          tag="h5"
          style="plain"
          textSize="sm"
          overrideFont="supercellTextBold"
          lineHeight="normal"
          className={styles.stampDescriptionHeading}
        >
          {t('streak_bonus')}
        </StoreTypography>
        <StoreTypography
          tag="p"
          style="plain"
          textSize="sm"
          overrideFont="supercellText"
          lineHeight="normal"
          className={styles.stampDescriptionText}
        >
          {t('streak_bonus_description')}
        </StoreTypography>
      </div>
    </div>
  )
}

ClashPassCard.displayName = 'ClashPassCard'
